import { decryptBackupPayload, encryptBackupPayload, PBKDF2_ITERATIONS } from './backupCrypto.ts'
import { bytesToBase64, base64ToBytes } from '../crypto/base64.ts'
import { serializeMasterPasswordKdf } from '../crypto/pbkdf2Kdf.ts'
import { getStableUserKey } from '../identity/ZionIdentityManager.ts'
import {
  getMasterPasswordKdfRecord,
  getMasterPasswordHash,
  getNickBase,
  getNickNumber,
  getPrivateKeyBase64,
  getPublicKeyBase64,
  importIdentityFromBackup,
} from '../storage/keyStore.ts'
import { replaceAllContacts, listContacts, type ZionContact } from '../storage/pairingStore.ts'
import { replaceAllMessages, loadMessages, type ChatMessage } from '../storage/chatStore.ts'
import {
  getDailyCode,
  getInboundQueueId,
  getSessionToken,
  getQueueOwnerPubKey,
  saveDailyCode,
  saveInboundQueueId,
  saveQueueOwnerPubKey,
  saveSessionToken,
} from '../storage/sessionStore.ts'
import { parseThemeId, applyTheme, type ZionThemeId } from '../ui/themes.ts'
import { markSessionUnlocked } from '../identity/ZionIdentityManager.ts'

export const BACKUP_FORMAT = 'zion-chat-backup-v2'
export const BACKUP_VERSION = 2
/** Android esporta `.enc`; web `.enc.json` — iOS PWA richiede entrambi in accept. */
export const BACKUP_FILE_ACCEPT =
  '.json,.enc,.enc.json,application/json,application/octet-stream,text/plain'

export interface BackupResult {
  ok: boolean
  message: string
  chatCount?: number
  messageCount?: number
  contactCount?: number
}

interface BackupMessageWire {
  id: string | number
  peer?: string
  text: string
  outgoing: boolean
  at: number
  pending?: boolean
  replyToId?: string
  replyPreview?: string
  mediaKind?: string
  mediaMime?: string
  mediaFileName?: string
  mediaGzip?: boolean
  mediaOrigSize?: number
  viewTtlMs?: number
  duration_seconds?: number
  durationSeconds?: number
  countdown_started_at?: number
  countdownStartedAt?: number
  read_and_destroyed?: boolean
  readAndDestroyed?: boolean
  audioDurationMs?: number
}

function b64Encode(bytes: Uint8Array): string {
  return bytesToBase64(bytes)
}

function b64Decode(text: string): Uint8Array {
  return base64ToBytes(text)
}

function formatExportTime(ms: number): string {
  return new Date(ms).toLocaleString('it-IT')
}

export function defaultBackupFileName(userKey: string): string {
  const nick = userKey.trim().toLowerCase().replace(/[^a-z0-9#-]+/g, '-') || 'zion'
  const ts = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '-')
  return `zion-backup-${nick}-${ts}.enc.json`
}

async function buildExportJson(userKey: string, theme: ZionThemeId): Promise<Record<string, unknown>> {
  const contacts = await listContacts()
  const peers = new Set<string>(contacts.map((c) => c.contactId))
  const chats: Record<string, BackupMessageWire[]> = {}
  let messageCount = 0

  for (const peer of peers) {
    const msgs = await loadMessages(peer)
    if (!msgs.length) continue
    chats[peer] = msgs.map((m) => ({
      id: m.id,
      peer: m.contactId,
      text: m.text,
      outgoing: m.outgoing,
      at: m.timestamp,
      pending: m.pending,
      replyToId: m.replyToId,
      replyPreview: m.replyPreview,
      mediaKind: m.mediaKind,
      mediaMime: m.mediaMime,
      mediaFileName: m.mediaFileName,
      mediaGzip: m.mediaGzip,
      mediaOrigSize: m.mediaOrigSize,
      viewTtlMs: m.viewTtlMs,
      duration_seconds: m.durationSeconds,
      countdown_started_at: m.countdownStartedAt,
      read_and_destroyed: m.readAndDestroyed,
      audioDurationMs: m.audioDurationMs,
    }))
    messageCount += msgs.length
  }

  const [sessionToken, inboundQueueId, dailyCode, nickBase, nickNumber] = await Promise.all([
    getSessionToken(),
    getInboundQueueId(),
    getDailyCode(),
    getNickBase(),
    getNickNumber(),
  ])
  const [privateKey, publicKey, masterKdf, masterHashLegacy] = await Promise.all([
    getPrivateKeyBase64(),
    getPublicKeyBase64(),
    getMasterPasswordKdfRecord(),
    getMasterPasswordHash(),
  ])

  const queueOwnerPubKey = await getQueueOwnerPubKey()

  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    user: userKey,
    exportedAt: Date.now(),
    chats,
    contacts: {},
    pairingContacts: contacts.map((c) => ({
      contactId: c.contactId,
      publicKey: c.publicKey,
      nickname: c.nickname,
      pairedAt: c.pairedAt,
      sessionToken: c.sessionToken,
      inboundQueueId: c.inboundQueueId,
      customName: c.customName ?? '',
    })),
    identity: {
      sessionToken: sessionToken ?? '',
      inboundQueueId: inboundQueueId ?? '',
      nickname: nickBase && nickNumber ? `${nickBase}#${nickNumber}` : userKey,
      dailyCode: dailyCode ?? '',
      queueOwnerPubKey: queueOwnerPubKey ?? '',
    },
    webKeys: {
      privateKey: privateKey ?? '',
      publicKey: publicKey ?? '',
      nickBase: nickBase ?? '',
      nickNumber: String(nickNumber || 0),
      masterPasswordKdf: masterKdf ? serializeMasterPasswordKdf(masterKdf) : '',
      masterPasswordHash: masterKdf ? '' : (masterHashLegacy ?? ''),
    },
    theme,
    messageCount,
  }
}

export async function exportEncryptedBackup(passphrase: string, theme: ZionThemeId): Promise<{
  fileName: string
  blob: Blob
  result: BackupResult
}> {
  if (!passphrase.trim()) {
    return {
      fileName: 'zion-backup.enc.json',
      blob: new Blob(),
      result: { ok: false, message: 'Inserisci una passphrase per cifrare il backup.' },
    }
  }
  const userKey = await getStableUserKey()
  if (!userKey) {
    return {
      fileName: 'zion-backup.enc.json',
      blob: new Blob(),
      result: { ok: false, message: 'Identità non configurata.' },
    }
  }

  const inner = await buildExportJson(userKey, theme)
  const plaintext = new TextEncoder().encode(JSON.stringify(inner, null, 2))
  const encrypted = await encryptBackupPayload(plaintext, passphrase)
  const wrapper = {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    kdf: 'PBKDF2-HMAC-SHA256',
    kdfIterations: PBKDF2_ITERATIONS,
    data: b64Encode(encrypted),
  }
  const chatCount = Object.keys(inner.chats as object).length
  const messageCount = Number(inner.messageCount ?? 0)
  const fileName = defaultBackupFileName(userKey)
  const blob = new Blob([JSON.stringify(wrapper, null, 2)], { type: 'application/json' })
  return {
    fileName,
    blob,
    result: {
      ok: true,
      message: `Backup salvato — ${formatExportTime(Number(inner.exportedAt))} (${chatCount} chat, ${messageCount} messaggi)`,
      chatCount,
      messageCount,
      contactCount: (inner.pairingContacts as unknown[]).length,
    },
  }
}

function wireToChatMessage(peer: string, row: BackupMessageWire): ChatMessage {
  const id =
    typeof row.id === 'string' && row.id.trim()
      ? row.id.trim()
      : String(row.id || row.at || Date.now())
  const durationSeconds =
    row.duration_seconds && row.duration_seconds > 0
      ? row.duration_seconds
      : row.durationSeconds && row.durationSeconds > 0
        ? row.durationSeconds
        : undefined
  const countdownStartedAt =
    row.countdown_started_at && row.countdown_started_at > 0
      ? row.countdown_started_at
      : row.countdownStartedAt && row.countdownStartedAt > 0
        ? row.countdownStartedAt
        : undefined
  return {
    id,
    contactId: (row.peer ?? peer).trim(),
    text: row.text ?? '',
    outgoing: !!row.outgoing,
    timestamp: row.at || Date.now(),
    pending: row.pending,
    replyToId: row.replyToId,
    replyPreview: row.replyPreview,
    mediaKind: row.mediaKind,
    mediaMime: row.mediaMime,
    mediaFileName: row.mediaFileName,
    mediaGzip: row.mediaGzip,
    mediaOrigSize: row.mediaOrigSize,
    viewTtlMs: row.viewTtlMs,
    durationSeconds,
    countdownStartedAt,
    readAndDestroyed: !!(row.read_and_destroyed ?? row.readAndDestroyed),
    audioDurationMs: row.audioDurationMs,
  }
}

function hasMessagePayload(row: BackupMessageWire): boolean {
  return !!(row.text?.trim() || row.mediaKind?.trim())
}

/** Allinea chiavi chat Android (nickname/token) al contactId canonico. */
function remapChatPeers(
  chatsObj: Record<string, unknown>,
  pairingArr: unknown,
): Map<string, unknown[]> {
  const aliasToId = new Map<string, string>()
  if (Array.isArray(pairingArr)) {
    for (const item of pairingArr) {
      if (!item || typeof item !== 'object') continue
      const o = item as Record<string, unknown>
      const id = String(o.contactId ?? '').trim().toLowerCase()
      if (!id) continue
      aliasToId.set(id, id)
      const nick = String(o.nickname ?? '').trim().toLowerCase()
      if (nick) aliasToId.set(nick, id)
      const token = String(o.sessionToken ?? '').trim().toLowerCase()
      if (token) aliasToId.set(token, id)
    }
  }

  const result = new Map<string, unknown[]>()
  for (const [rawKey, arr] of Object.entries(chatsObj)) {
    if (!Array.isArray(arr)) continue
    const key = rawKey.trim()
    if (!key) continue
    const canonical = aliasToId.get(key.toLowerCase()) ?? key.toLowerCase()
    const existing = result.get(canonical)
    result.set(canonical, existing ? [...existing, ...arr] : [...arr])
  }
  return result
}

async function readBackupFileText(file: File): Promise<string> {
  try {
    return await file.text()
  } catch {
    const buf = await file.arrayBuffer()
    return new TextDecoder().decode(buf)
  }
}

async function parseBackupRootFlexible(
  file: File,
  passphrase: string,
): Promise<Record<string, unknown> | null> {
  const rawBytes = new Uint8Array(await file.arrayBuffer())
  const rawText = new TextDecoder().decode(rawBytes).replace(/^\uFEFF/, '').trim()
  let wrapper: Record<string, unknown> | null = null
  // iOS/Safari a volte “sporca” il file con prefissi prima di `{`.
  // Cerchiamo il primo `{` e proviamo comunque a fare JSON parse.
  const firstBraceIdx = rawText.indexOf('{')
  const maybeJson = firstBraceIdx >= 0 ? rawText.slice(firstBraceIdx) : ''
  if (maybeJson.startsWith('{')) {
    try {
      wrapper = JSON.parse(maybeJson) as Record<string, unknown>
    } catch {
      wrapper = null
    }
  }

  if (wrapper) {
    const format = String(wrapper.format ?? '')
    if (format === BACKUP_FORMAT) {
      const encrypted = b64Decode(String(wrapper.data ?? ''))
      const iterations = Number(wrapper.kdfIterations ?? 0) || undefined
      const plain = await decryptBackupPayload(encrypted, passphrase, iterations)
      return JSON.parse(new TextDecoder().decode(plain)) as Record<string, unknown>
    }
    if (format === 'zion-chat-backup') {
      return wrapper
    }
  }

  // Fallback legacy: file .enc con payload cifrato puro (senza wrapper JSON).
  try {
    const plain = await decryptBackupPayload(rawBytes, passphrase)
    return JSON.parse(new TextDecoder().decode(plain)) as Record<string, unknown>
  } catch {
    // Fallback extra: file contiene solo base64 del payload cifrato.
    try {
      const compact = rawText.replace(/\s+/g, '')
      if (!compact || compact.length < 32 || compact.length % 4 !== 0) return null
      const encrypted = b64Decode(compact)
      const plain = await decryptBackupPayload(encrypted, passphrase)
      return JSON.parse(new TextDecoder().decode(plain)) as Record<string, unknown>
    } catch {
      // Ancora: potrebbe essere una "JSON string" contenente base64 (es. "\"QUJD...\"").
      try {
        const parsed = JSON.parse(rawText) as unknown
        if (typeof parsed === 'string') {
          const compact = parsed.replace(/\s+/g, '')
          if (compact && compact.length % 4 === 0) {
            const encrypted = b64Decode(compact)
            const plain = await decryptBackupPayload(encrypted, passphrase)
            return JSON.parse(new TextDecoder().decode(plain)) as Record<string, unknown>
          }
        }
      } catch {
        /* ignore */
      }
      return null
    }
  }
}

function hasRestorablePayload(
  root: Record<string, unknown>,
  contacts: ZionContact[],
  allMessages: ChatMessage[],
): boolean {
  if (contacts.length || allMessages.length) return true
  const webKeys = root.webKeys as Record<string, string> | undefined
  if (webKeys?.privateKey?.trim() && webKeys.publicKey?.trim()) return true
  const identity = root.identity as Record<string, string> | undefined
  if (identity?.sessionToken?.trim() || identity?.inboundQueueId?.trim()) return true
  return false
}

function parseContact(raw: unknown): ZionContact | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const contactId = String(o.contactId ?? '').trim()
  const publicKey = String(o.publicKey ?? '').trim()
  const sessionToken = String(o.sessionToken ?? '').trim()
  if (!contactId || !publicKey || !sessionToken) return null
  return {
    contactId,
    publicKey,
    sessionToken,
    nickname: String(o.nickname ?? '').trim(),
    pairedAt: Number(o.pairedAt) || Date.now(),
    inboundQueueId: String(o.inboundQueueId ?? '').trim(),
    customName: String(o.customName ?? '').trim() || undefined,
  }
}

export async function importEncryptedBackup(
  file: File,
  passphrase: string,
  opts: { allowUserMismatch?: boolean } = {},
): Promise<BackupResult> {
  if (!passphrase.trim()) {
    return { ok: false, message: 'Inserisci la passphrase del backup.' }
  }
  let root: Record<string, unknown> | null = null
  try {
    root = await parseBackupRootFlexible(file, passphrase)
  } catch {
    return { ok: false, message: 'Passphrase errata — impossibile decifrare il backup.' }
  }
  if (!root) {
    const raw = (await readBackupFileText(file)).replace(/^\uFEFF/, '').trim()
    return raw.startsWith('{')
      ? { ok: false, message: 'Formato backup non riconosciuto.' }
      : { ok: false, message: 'File backup non valido o passphrase errata.' }
  }

  const backupUser = String(root.user ?? '').trim().toLowerCase()
  const currentUser = (await getStableUserKey()).trim().toLowerCase()
  if (currentUser && backupUser && backupUser !== currentUser && !opts.allowUserMismatch) {
    return {
      ok: false,
      message: `Backup di @${backupUser} — identità attuale @${currentUser}. Conferma il ripristino.`,
    }
  }

  const pairingArr = root.pairingContacts
  const contacts: ZionContact[] = []
  if (Array.isArray(pairingArr)) {
    for (const item of pairingArr) {
      const c = parseContact(item)
      if (c) contacts.push(c)
    }
  }

  const chatsObj = root.chats
  const allMessages: ChatMessage[] = []
  if (chatsObj && typeof chatsObj === 'object') {
    const remapped = remapChatPeers(chatsObj as Record<string, unknown>, pairingArr)
    for (const [peer, arr] of remapped) {
      for (const item of arr) {
        if (!item || typeof item !== 'object') continue
        const row = item as BackupMessageWire
        if (!hasMessagePayload(row)) continue
        allMessages.push(wireToChatMessage(peer, row))
      }
    }
  }

  if (!hasRestorablePayload(root, contacts, allMessages)) {
    return { ok: false, message: 'Nessun dato da ripristinare nel backup.' }
  }

  const webKeys = root.webKeys as Record<string, string> | undefined
  if (webKeys?.privateKey && webKeys.publicKey) {
    await importIdentityFromBackup({
      privateKeyBase64: webKeys.privateKey,
      publicKeyBase64: webKeys.publicKey,
      nickBase: webKeys.nickBase,
      nickNumber: parseInt(webKeys.nickNumber ?? '0', 10),
      masterPasswordKdf: String(webKeys.masterPasswordKdf ?? ''),
      masterPasswordHash: webKeys.masterPasswordHash,
    })
    markSessionUnlocked()
  }

  const identity = root.identity as Record<string, string> | undefined
  if (identity) {
    const sessionToken = identity.sessionToken?.trim()
    const queueId = identity.inboundQueueId?.trim()
    const dailyCode = identity.dailyCode?.trim()
    const queueOwnerPubKey = identity.queueOwnerPubKey?.trim()
    if (sessionToken) await saveSessionToken(sessionToken)
    if (queueId) await saveInboundQueueId(queueId)
    if (dailyCode) await saveDailyCode(dailyCode)
    if (queueOwnerPubKey) await saveQueueOwnerPubKey(queueOwnerPubKey)
  }

  if (contacts.length) await replaceAllContacts(contacts)
  if (allMessages.length) await replaceAllMessages(allMessages)

  const theme = parseThemeId(String(root.theme ?? ''))
  applyTheme(theme)

  const chatPeers = new Set(allMessages.map((m) => m.contactId))
  return {
    ok: true,
    message: `Ripristinato (${chatPeers.size} chat, ${allMessages.length} messaggi, ${contacts.length} contatti)`,
    chatCount: chatPeers.size,
    messageCount: allMessages.length,
    contactCount: contacts.length,
  }
}

export function downloadBackupBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}
